import type { AggregatedBundlePriceDto } from '~/models/ListingProduct.api.model';
import type {
  PriceModalInput,
  PriceModalPeriodTotalFromBackend,
  PriceModalPlanInput,
  PriceModalResult,
} from '~/models/pricing/PriceDetailsModalTypes.model';
import { buildPriceModal } from '~/utils/pricing/priceDetailsModal';

function aggregateTotalSavings(
  parentBP: AggregatedBundlePriceDto | null,
  familyBP: AggregatedBundlePriceDto | null,
  includeParent: boolean,
  numConnections: number,
): number | undefined {
  let total = 0;

  if (includeParent && parentBP) {
    total += parentBP.Savings;
  }

  if (familyBP) {
    total += familyBP.Savings * numConnections;
  }

  return total > 0 ? total : undefined;
}

function buildFamilyPeriodTotals(
  parentBP: AggregatedBundlePriceDto | null,
  familyBP: AggregatedBundlePriceDto,
  includeParent: boolean,
  familyDiscountedTotal: number,
  familyRegularTotal: number,
): PriceModalPeriodTotalFromBackend[] | undefined {
  const parentDiscountMonths = includeParent
    ? (parentBP?.Plan?.OfferValidityInMonthsDiscount ?? 0)
    : 0;

  const familyDiscountMonths =
    familyBP.Plan?.OfferValidityInMonthsDiscount ?? 0;

  const endMonths = [
    ...new Set(
      [parentDiscountMonths, familyDiscountMonths].filter((m) => m > 0),
    ),
  ].sort((a, b) => a - b);

  if (endMonths.length === 0) {
    return undefined;
  }

  const parentMonthly = includeParent ? (parentBP?.Plan?.MonthlyFee ?? 0) : 0;
  const parentRegular = includeParent
    ? parentBP?.Plan?.RegularMonthlyFee || parentBP?.Plan?.MonthlyFee || 0
    : 0;

  const discountedTotal = parentMonthly + familyDiscountedTotal;
  const regularTotal = parentRegular + familyRegularTotal;

  if (endMonths.length === 1) {
    return [
      {
        periodEndMonth: endMonths[0],
        monthlyTotal: discountedTotal,
        regularMonthlyTotal:
          regularTotal > discountedTotal ? regularTotal : undefined,
      },
    ];
  }

  // Two different discount periods — need intermediate total.
  // The plan whose discount expired first switches to its regular fee.
  const firstEndMonth = endMonths[0];

  const parentFeeIntermediate =
    parentDiscountMonths === firstEndMonth ? parentRegular : parentMonthly;
  const familyFeeIntermediate =
    familyDiscountMonths === firstEndMonth
      ? familyRegularTotal
      : familyDiscountedTotal;

  const intermediateTotal =
    (includeParent ? parentFeeIntermediate : 0) + familyFeeIntermediate;

  return [
    {
      periodEndMonth: firstEndMonth,
      monthlyTotal: discountedTotal,
      regularMonthlyTotal:
        regularTotal > discountedTotal ? regularTotal : undefined,
    },
    {
      periodEndMonth: endMonths[1],
      monthlyTotal: intermediateTotal,
      regularMonthlyTotal:
        regularTotal > intermediateTotal ? regularTotal : undefined,
    },
  ];
}

function aggregateTotalCost(
  parentBP: AggregatedBundlePriceDto | null,
  familyBP: AggregatedBundlePriceDto | null,
  includeParent: boolean,
  numConnections: number,
): number | undefined {
  let total = 0;

  if (includeParent && parentBP) {
    total += parentBP.TotalCost;
  }

  if (familyBP) {
    total += familyBP.TotalCost * numConnections;
  }

  return total || undefined;
}

export function useFamilyPriceDetailsModal() {
  const familyStore = useFamilyStore();
  const planStore = usePlanStore();

  const priceDetails = computed<PriceModalResult | null>(() => {
    const parentBP = familyStore.selectedParentPlanBundlePrice;
    const familyBP = familyStore.selectedFamilyPlanBundlePrice;
    const bindingPeriod = familyStore.selectedBindingPeriod;
    const includeParent = familyStore.shouldIncludeParentPlanInPricing;
    const numConnections = familyStore.numberOfFamilyPlanConnections;

    if (!familyBP?.Plan) {
      return null;
    }

    const plans: PriceModalPlanInput[] = [];

    if (includeParent && parentBP?.Plan) {
      plans.push({
        planName: planStore.selectPlanProductName(
          parentBP.Plan.PlanId,
          parentBP.Plan.PlanName,
        ),
        monthlyFee: parentBP.Plan.MonthlyFee,
        regularMonthlyFee: parentBP.Plan.RegularMonthlyFee,
        offerValidityInMonthsDiscount:
          parentBP.Plan.OfferValidityInMonthsDiscount ?? 0,
        quantity: 1,
        dataAllowance: parentBP.Plan.DataAllowance,
        regularDataAllowance: parentBP.Plan.RegularDataAllowance,
        isFamily: parentBP.Plan.IsFamilyPlan,
      });
    }

    plans.push({
      planName: planStore.selectPlanProductName(
        familyBP.Plan.PlanId,
        familyBP.Plan.PlanName,
      ),
      monthlyFee: familyBP.Plan.MonthlyFee,
      regularMonthlyFee: familyBP.Plan.RegularMonthlyFee,
      offerValidityInMonthsDiscount:
        familyBP.Plan.OfferValidityInMonthsDiscount ?? 0,
      quantity: numConnections,
      dataAllowance: familyBP.Plan.DataAllowance,
      regularDataAllowance: familyBP.Plan.RegularDataAllowance,
      isFamily: familyBP.Plan.IsFamilyPlan,
    });

    const input: PriceModalInput = {
      plans,
      device: undefined,
      bindingPeriodMonths: bindingPeriod,
      totalCostFromBackend: aggregateTotalCost(
        parentBP,
        familyBP,
        includeParent,
        numConnections,
      ),
      totalSavingsFromBackend: aggregateTotalSavings(
        parentBP,
        familyBP,
        includeParent,
        numConnections,
      ),
      periodTotalsFromBackend: buildFamilyPeriodTotals(
        parentBP,
        familyBP,
        includeParent,
        familyStore.priceOfAddedFamilyConnections,
        familyStore.regularPriceOfAddedFamilyConnections ||
          familyStore.priceOfAddedFamilyConnections,
      ),
    };

    return buildPriceModal(input);
  });

  return { priceDetails };
}
